import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { ArrowLeft, Package, Truck, Copy, Check, ExternalLink, Loader2, RefreshCw, RotateCcw, CreditCard, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIPPING_STATUS_META = {
  pending:         { label: 'Pendiente',        className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  label_generated: { label: 'Guía Generada',    className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  in_transit:      { label: 'En Tránsito',      className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  delivered:       { label: 'Entregado',        className: 'bg-green-500/15 text-green-300 border-green-500/30' },
  failed:          { label: 'Fallido',          className: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const RETURN_STATUS_META = {
  requested: { label: 'Solicitada', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  approved:  { label: 'Aprobada',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  rejected:  { label: 'Rechazada',  className: 'bg-red-500/15 text-red-300 border-red-500/30' },
  completed: { label: 'Completada', className: 'bg-green-500/15 text-green-300 border-green-500/30' },
};

const RETURN_REASONS = [
  { value: 'producto_danado', label: 'Producto dañado' },
  { value: 'producto_incorrecto', label: 'Producto incorrecto' },
  { value: 'no_cumple_expectativas', label: 'No cumple expectativas' },
  { value: 'otro', label: 'Otro' },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [tracking, setTracking] = useState(null);

  // ---- Payment state (Stripe) ----
  const [payingNow, setPayingNow] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);
  const pollIntervalRef = useRef(null);
  const initialPaymentParamRef = useRef(null);

  // ---- Returns state ----
  const [returnRequest, setReturnRequest] = useState(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('producto_danado');
  const [returnComments, setReturnComments] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);

  const fetchReturn = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/returns/by-order/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setReturnRequest(r.data || null);
    } catch {
      setReturnRequest(null);
    }
  }, [id, token]);

  useEffect(() => {
    axios.get(`${API}/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setOrder(r.data); setLoading(false); })
      .catch(() => { setLoading(false); navigate('/perfil'); });
    fetchReturn();
  }, [id, token, navigate, fetchReturn]);

  // ---- Read ?payment=success | ?payment=cancelled on mount and notify user ----
  // We capture it once, show a toast, and clear it from the URL so refreshes
  // don't replay the toast. When payment=success, kick off a short polling
  // window to wait for the webhook to flip payment_status → 'paid'.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentParam = params.get('payment');
    if (!paymentParam || initialPaymentParamRef.current) return;
    initialPaymentParamRef.current = paymentParam;

    if (paymentParam === 'success') {
      toast.success('¡Pago recibido! Estamos confirmando con la pasarela…');
      setPollingPayment(true);
    } else if (paymentParam === 'cancelled' || paymentParam === 'canceled') {
      toast.warning('Pago cancelado. Tu pedido sigue pendiente: puedes reintentar el pago cuando quieras.');
    } else if (paymentParam === 'failed') {
      toast.error('El pago no pudo procesarse. Por favor inténtalo de nuevo.');
    }

    // Clean the query string (replaceState to preserve history)
    if (window.history?.replaceState) {
      const url = `${window.location.pathname}${window.location.hash || ''}`;
      window.history.replaceState({}, '', url);
    }
  }, [location.search]);

  // ---- Poll /api/payments/status while we expect a webhook ----
  useEffect(() => {
    if (!pollingPayment) return undefined;
    let attempts = 0;
    const maxAttempts = 20; // ~60s (3s interval)
    pollIntervalRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ps = res.data?.payment_status;
        if (ps === 'paid') {
          // Refresh full order to update UI
          const fresh = await axios.get(`${API}/orders/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrder(fresh.data);
          setPollingPayment(false);
          toast.success('Pago confirmado correctamente ✅');
          clearInterval(pollIntervalRef.current);
        } else if (attempts >= maxAttempts) {
          setPollingPayment(false);
          clearInterval(pollIntervalRef.current);
          toast.message('Seguimos esperando la confirmación. Recarga la página en unos minutos.');
        }
      } catch {
        // Silent — keep retrying
      }
    }, 3000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [pollingPayment, id, token]);

  const handlePayNow = async () => {
    setPayingNow(true);
    try {
      const res = await axios.post(
        `${API}/payments/create-session`,
        { order_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data?.session_url;
      if (!url) {
        toast.error('No se pudo obtener el enlace de pago. Intenta de nuevo.');
        return;
      }
      toast.success('Redirigiendo a la pasarela de pago…');
      window.location.href = url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al iniciar el pago');
    } finally {
      setPayingNow(false);
    }
  };

  const handleSubmitReturn = async () => {
    setSubmittingReturn(true);
    try {
      const res = await axios.post(`${API}/returns/request`, {
        order_id: id,
        reason: returnReason,
        comments: returnComments,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setReturnRequest(res.data);
      setReturnDialogOpen(false);
      setReturnComments('');
      toast.success('Solicitud de devolución enviada correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error enviando solicitud');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const handleCopyTracking = async () => {
    if (!order?.envia_tracking_number) return;
    try {
      await navigator.clipboard.writeText(order.envia_tracking_number);
      setCopied(true);
      toast.success('Número de rastreo copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const handleTrack = async () => {
    if (!order?.envia_tracking_number) return;
    setTrackLoading(true);
    setTracking(null);
    try {
      const res = await axios.get(`${API}/envia/track/${encodeURIComponent(order.envia_tracking_number)}`);
      setTracking(res.data);
      if (!res.data.events?.length) {
        toast.message(res.data.message || 'Sin eventos aún');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error consultando rastreo');
    } finally {
      setTrackLoading(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground">Cargando...</div>;
  if (!order) return null;

  const statusLabels = { pendiente: 'Pendiente', pagado: 'Pagado', cancelado: 'Cancelado', en_proceso: 'En Proceso', enviado: 'Enviado', entregado: 'Entregado', compra_interna: 'Compra Interna' };
  const shippingMeta = SHIPPING_STATUS_META[order.shipping_status] || SHIPPING_STATUS_META.pending;

  // Payment UI helpers
  const isInternalOrder = order.order_type === 'interna';
  const isPaid = order.payment_status === 'paid' || order.payment_status === 'pagado';
  const isCancelledOrder = order.order_status === 'cancelado';
  const showPayNow = !isInternalOrder && !isPaid && !isCancelledOrder;
  const showPaidBanner = !isInternalOrder && isPaid;

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6 gap-2"><ArrowLeft className="h-4 w-4" /> Volver</Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-display font-semibold">Orden {order.order_number}</h1>
            <p className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleString('es-MX')}</p>
          </div>
          <Badge variant={order.order_type === 'interna' ? 'outline' : 'secondary'}>
            {statusLabels[order.order_status] || order.order_status}
          </Badge>
        </div>

        {/* ---- Pending payment notice (with Pagar ahora button) ---- */}
        {showPayNow && (
          <Card
            className="mb-6 border-blue-800 bg-blue-950/40"
            data-testid="pending-payment-card"
          >
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="p-2 rounded-md bg-blue-500/15 shrink-0">
                    <AlertCircle className="h-4 w-4 text-blue-300" />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <h3 className="font-display font-semibold text-blue-300">
                      Este pedido está pendiente de pago
                    </h3>
                    <p className="text-sm text-blue-300/80 leading-relaxed">
                      Haz clic en <strong>“Pagar ahora”</strong> para completar tu compra a través de
                      Stripe. Tu pedido se mantendrá reservado mientras tanto.
                    </p>
                    {pollingPayment && (
                      <p
                        className="text-xs text-blue-300/70 flex items-center gap-1.5 pt-1"
                        data-testid="payment-polling-indicator"
                      >
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Verificando estado del pago…
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={handlePayNow}
                  disabled={payingNow}
                  className="shrink-0 gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="pay-now-button"
                >
                  {payingNow ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  {payingNow ? 'Redirigiendo…' : 'Pagar ahora'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ---- Paid banner (confirmation) ---- */}
        {showPaidBanner && (
          <Card
            className="mb-6 border-green-500/40 bg-green-500/5"
            data-testid="paid-confirmation-card"
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-green-500/15 shrink-0">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-display font-semibold text-green-100">Pago confirmado</h3>
                  <p className="text-sm text-green-200/80">
                    Procesado mediante <strong>Stripe</strong>
                    {order.payment_date ? ` el ${new Date(order.payment_date).toLocaleString('es-MX')}` : ''}.
                    {order.payment_reference && (
                      <>
                        {' · Referencia: '}
                        <code className="font-mono text-xs">{order.payment_reference}</code>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-display font-semibold mb-4">Productos</h3>
              <div className="space-y-3">
                {order.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                    <Package className="h-8 w-8 text-muted-foreground/30 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">{item.sku}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p>{item.quantity} x ${item.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                      <p className="font-semibold">${item.item_total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-display font-semibold mb-4">Resumen</h3>
                <div className="space-y-2 text-sm" data-testid="customer-order-summary-mxn">
                  {(() => {
                    const subtotalMXN = order.subtotal_mxn ?? (order.subtotal_usd && order.exchange_rate_used
                      ? order.subtotal_usd * order.exchange_rate_used
                      : order.subtotal);
                    const discountMXN = order.discount_mxn ?? (order.discount_amount && order.exchange_rate_used
                      ? order.discount_amount * order.exchange_rate_used
                      : order.discount_amount);
                    const shippingMXN = order.shipping_cost_mxn ?? order.envia_price_mxn ?? order.shipping_cost ?? 0;
                    const insuranceMXN = order.insurance_cost_mxn ?? 0;
                    const totalMXN = order.total_mxn ?? order.total ?? 0;
                    const fmtMXN = (n) =>
                      `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span data-testid="customer-subtotal-mxn">{fmtMXN(subtotalMXN)}</span>
                        </div>
                        {discountMXN > 0 && (
                          <div className="flex justify-between text-green-400">
                            <span>Descuento ({order.discount_info?.percent}%)</span>
                            <span>-{fmtMXN(discountMXN)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Envío</span>
                          <span>{shippingMXN > 0 ? fmtMXN(shippingMXN) : 'Gratis'}</span>
                        </div>
                        {order.insured && insuranceMXN > 0 && (
                          <div className="flex justify-between text-blue-300">
                            <span>Seguro</span>
                            <span>{fmtMXN(insuranceMXN)}</span>
                          </div>
                        )}
                        <div className="border-t pt-2 flex justify-between font-display font-semibold">
                          <span>Total</span>
                          <span data-testid="customer-total-mxn">{fmtMXN(totalMXN)}</span>
                        </div>
                        {order.exchange_rate_used ? (
                          <p
                            className="text-[11px] text-muted-foreground italic leading-snug pt-1"
                            data-testid="customer-order-rate-note"
                          >
                            Tipo de cambio usado: ${Number(order.exchange_rate_used).toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} MXN/USD
                          </p>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-display font-semibold mb-3">Información</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Tipo:</span> {order.order_type === 'interna' ? 'Compra Interna' : 'Compra Normal'}</div>
                  <div><span className="text-muted-foreground">Pago:</span> {order.payment_status === 'no_aplica' ? 'No aplica' : order.payment_status}</div>
                  {order.shipping_info && <div><span className="text-muted-foreground">Envío:</span> {order.shipping_info.name || order.shipping_info.carrier} {order.shipping_info.estimated_delivery && `(${order.shipping_info.estimated_delivery})`}</div>}
                  {order.shipping_address && <div><span className="text-muted-foreground">Dirección:</span> {order.shipping_address}</div>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ---- Envia tracking section ---- */}
        {order.envia_tracking_number && (
          <Card className="mt-6" data-testid="order-tracking-section">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-secondary"><Truck className="h-4 w-4" /></div>
                  <div>
                    <h3 className="font-display font-semibold">Rastreo de Envío</h3>
                    <p className="text-xs text-muted-foreground">
                      {order.envia_carrier?.toUpperCase()}
                      {order.envia_service ? ` • ${order.envia_service}` : ''}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className={shippingMeta.className} data-testid="shipping-status-badge">
                  {shippingMeta.label}
                </Badge>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Número de Rastreo</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-md bg-secondary text-sm font-mono break-all" data-testid="order-tracking-number">
                    {order.envia_tracking_number}
                  </code>
                  <Button variant="outline" size="sm" onClick={handleCopyTracking} className="shrink-0 gap-1.5" data-testid="copy-tracking-button">
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {order.envia_label_url && (
                  <a href={order.envia_label_url} target="_blank" rel="noopener noreferrer" data-testid="order-label-link">
                    <Button variant="secondary" size="sm" className="gap-2">
                      <ExternalLink className="h-3.5 w-3.5" /> Ver etiqueta PDF
                    </Button>
                  </a>
                )}
                <Button variant="outline" size="sm" onClick={handleTrack} disabled={trackLoading} className="gap-2" data-testid="track-button">
                  {trackLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Rastrear envío
                </Button>
              </div>

              {/* Tracking results */}
              {tracking && (
                <div className="border-t pt-4 mt-2 space-y-3" data-testid="tracking-results">
                  {tracking.current_status && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Estado actual: </span>
                      <span className="font-medium">{tracking.current_status}</span>
                    </p>
                  )}
                  {tracking.events?.length > 0 ? (
                    <ol className="space-y-2 text-sm">
                      {tracking.events.map((ev, i) => (
                        <li key={i} className="flex gap-3 border-l-2 border-border pl-3 py-1">
                          <div className="flex-1">
                            {ev.description && <p className="font-medium">{ev.description}</p>}
                            <p className="text-xs text-muted-foreground">
                              {ev.location ? `${ev.location} • ` : ''}
                              {ev.timestamp ? new Date(ev.timestamp).toLocaleString('es-MX') : ''}
                            </p>
                          </div>
                          {ev.status && <Badge variant="outline" className="text-xs">{ev.status}</Badge>}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">{tracking.message || 'Sin eventos registrados aún'}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ---- Returns section ---- */}
        {order.shipping_status === 'delivered' && (
          <Card className="mt-6" data-testid="order-returns-section">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-secondary"><RotateCcw className="h-4 w-4" /></div>
                  <div>
                    <h3 className="font-display font-semibold">Devoluciones</h3>
                    <p className="text-xs text-muted-foreground">
                      {returnRequest ? 'Estado de tu solicitud de devolución' : 'Tienes 30 días para solicitar una devolución'}
                    </p>
                  </div>
                </div>
                {returnRequest && RETURN_STATUS_META[returnRequest.status] && (
                  <Badge variant="outline" className={RETURN_STATUS_META[returnRequest.status].className} data-testid="return-status-badge">
                    {RETURN_STATUS_META[returnRequest.status].label}
                  </Badge>
                )}
              </div>

              {returnRequest ? (
                <div className="space-y-2 text-sm" data-testid="existing-return-info">
                  <div><span className="text-muted-foreground">Motivo: </span>{RETURN_REASONS.find(r => r.value === returnRequest.reason)?.label || returnRequest.reason}</div>
                  {returnRequest.comments && <div><span className="text-muted-foreground">Tus comentarios: </span>{returnRequest.comments}</div>}
                  {returnRequest.admin_notes && (
                    <div className="bg-secondary/40 border rounded-md p-3 mt-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Notas del equipo</p>
                      <p>{returnRequest.admin_notes}</p>
                    </div>
                  )}
                  {returnRequest.envia_return_tracking && (
                    <div>
                      <span className="text-muted-foreground">Guía de devolución: </span>
                      <code className="font-mono">{returnRequest.envia_return_tracking}</code>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Solicitada el {new Date(returnRequest.created_at).toLocaleString('es-MX')}</p>
                </div>
              ) : (
                <Button onClick={() => setReturnDialogOpen(true)} variant="outline" className="gap-2" data-testid="request-return-button">
                  <RotateCcw className="h-4 w-4" /> Solicitar Devolución
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Return request dialog */}
        <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
          <DialogContent data-testid="return-dialog">
            <DialogHeader>
              <DialogTitle>Solicitar Devolución</DialogTitle>
              <DialogDescription>
                Cuéntanos por qué necesitas devolver este pedido. Revisaremos tu solicitud y te contactaremos pronto.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="return-reason">Motivo *</Label>
                <Select value={returnReason} onValueChange={setReturnReason}>
                  <SelectTrigger id="return-reason" data-testid="return-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RETURN_REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="return-comments">Comentarios (opcional)</Label>
                <textarea
                  id="return-comments"
                  value={returnComments}
                  onChange={e => setReturnComments(e.target.value)}
                  rows={4}
                  maxLength={500}
                  placeholder="Describe brevemente el problema..."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="return-comments-input"
                />
                <p className="text-xs text-muted-foreground text-right">{returnComments.length}/500</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReturnDialogOpen(false)} disabled={submittingReturn}>Cancelar</Button>
              <Button onClick={handleSubmitReturn} disabled={submittingReturn} data-testid="submit-return-button">
                {submittingReturn ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
